import { watch } from 'vue'
import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteLocationRaw } from 'vue-router'
import { logEvent } from 'firebase/analytics'
import { analytics } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import PlaceholderView from '@/views/PlaceholderView.vue'
import ProfileView from '@/views/ProfileView.vue'
import LoginView from '@/views/LoginView.vue'
import OnboardingView from '@/views/OnboardingView.vue'
import IncidentListView from '@/views/IncidentListView.vue'
import CreateIncidentView from '@/views/CreateIncidentView.vue'
import WizardView from '@/views/WizardView.vue'
import IncidentLayout from '@/components/incident/IncidentLayout.vue'
import OverviewView from '@/views/OverviewView.vue'
import TeamsView from '@/views/TeamsView.vue'
import AssignmentsView from '@/views/AssignmentsView.vue'
import PersonnelView from '@/views/PersonnelView.vue'
import JoinView from '@/views/JoinView.vue'
import FormsView from '@/views/FormsView.vue'
import AgencyView from '@/views/AgencyView.vue'
import AgencyStatsView from '@/views/AgencyStatsView.vue'
import IncidentSettingsView from '@/views/IncidentSettingsView.vue'
import IncidentGeneralView from '@/views/settings/IncidentGeneralView.vue'
import IncidentOperationsView from '@/views/settings/IncidentOperationsView.vue'
import IncidentAuditView from '@/views/settings/IncidentAuditView.vue'
import ICS201View from '@/views/forms/ICS201View.vue'
import ICS211View from '@/views/forms/ICS211View.vue'
import ICS214View from '@/views/forms/ICS214View.vue'
import SAR104SView from '@/views/forms/SAR104SView.vue'
import SAR104SListView from '@/views/forms/SAR104SListView.vue'
import SAR110View from '@/views/forms/SAR110View.vue'
import SAR110ListView from '@/views/forms/SAR110ListView.vue'
import SAR111View from '@/views/forms/SAR111View.vue'
import SAR111ListView from '@/views/forms/SAR111ListView.vue'
import SAR134View from '@/views/forms/SAR134View.vue'
import IAPView from '@/views/forms/IAPView.vue'
import SubjectView from '@/views/SubjectView.vue'
import CluesView from '@/views/CluesView.vue'
import CommsView from '@/views/CommsView.vue'
import DevSeedView from '@/views/DevSeedView.vue'
import SuperAdminView from '@/views/SuperAdminView.vue'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresSuperAdmin?: boolean
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/incidents',
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { requiresAuth: false },
    },
    {
      path: '/showcase',
      name: 'showcase',
      component: () => import('@/views/ShowcaseView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/changelog',
      name: 'changelog',
      component: () => import('@/views/ChangelogView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: OnboardingView,
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: ProfileView,
      meta: { requiresAuth: true },
    },
    {
      path: '/profile',
      redirect: '/settings',
    },
    {
      path: '/agency',
      name: 'agency',
      component: AgencyView,
      meta: { requiresAuth: true },
    },
    {
      path: '/agency/stats',
      name: 'agency-stats',
      component: AgencyStatsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/super-admin',
      name: 'super-admin',
      component: SuperAdminView,
      meta: { requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/incidents',
      name: 'incident-list',
      component: IncidentListView,
      meta: { requiresAuth: true },
    },
    {
      path: '/incidents/new',
      name: 'incident-create',
      component: CreateIncidentView,
      meta: { requiresAuth: true },
    },
    {
      path: '/incidents/wizard',
      name: 'incident-wizard',
      component: WizardView,
      meta: { requiresAuth: true },
    },
    {
      path: '/incidents/:id/join',
      name: 'incident-join',
      component: JoinView,
      meta: { requiresAuth: false },
    },
    {
      path: '/incidents/:id',
      component: IncidentLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: (to) => ({ name: 'incident-overview', params: to.params }),
        },
        {
          path: 'overview',
          name: 'incident-overview',
          component: OverviewView,
        },
        {
          path: 'subjects',
          name: 'incident-subjects',
          component: SubjectView,
        },
        {
          path: 'subjects/:sid',
          name: 'incident-subject-detail',
          component: SubjectView,
        },
        {
          path: 'teams',
          name: 'incident-teams',
          component: TeamsView,
        },
        {
          path: 'assignments',
          name: 'incident-assignments',
          component: AssignmentsView,
        },
        {
          path: 'settings',
          name: 'incident-settings',
          component: IncidentSettingsView,
        },
        {
          path: 'settings/general',
          name: 'incident-settings-general',
          component: IncidentGeneralView,
        },
        {
          path: 'settings/operations',
          name: 'incident-settings-operations',
          component: IncidentOperationsView,
        },
        {
          path: 'settings/audit',
          name: 'incident-settings-audit',
          component: IncidentAuditView,
        },
        {
          path: 'settings/personnel',
          name: 'incident-settings-personnel',
          component: PersonnelView,
        },
        // Legacy redirects
        {
          path: 'personnel',
          redirect: (to) => ({ name: 'incident-settings-personnel', params: to.params }),
        },
        {
          path: 'incident',
          redirect: (to) => ({ name: 'incident-settings', params: to.params }),
        },
        {
          path: 'ops',
          redirect: (to) => ({ name: 'incident-settings', params: to.params }),
        },
        {
          path: 'ops/:opId',
          redirect: (to) => ({ name: 'incident-settings', params: to.params }),
        },
        {
          path: 'forms',
          name: 'incident-forms',
          component: FormsView,
        },
        {
          path: 'forms/ICS201',
          name: 'incident-form-ics201',
          component: ICS201View,
        },
        {
          path: 'forms/ICS211',
          name: 'incident-form-ics211',
          component: ICS211View,
        },
        {
          path: 'forms/ICS214',
          name: 'incident-form-ics214',
          component: ICS214View,
        },
        {
          path: 'forms/SAR104S',
          name: 'incident-form-sar104s-list',
          component: SAR104SListView,
        },
        {
          path: 'forms/SAR104S/:assignmentId',
          name: 'incident-form-sar104s',
          component: SAR104SView,
        },
        {
          path: 'forms/SAR110',
          name: 'incident-form-sar110-list',
          component: SAR110ListView,
        },
        {
          path: 'forms/SAR110/:taId',
          name: 'incident-form-sar110',
          component: SAR110View,
        },
        {
          path: 'forms/SAR111',
          name: 'incident-form-sar111-list',
          component: SAR111ListView,
        },
        {
          path: 'forms/SAR111/:taId',
          name: 'incident-form-sar111',
          component: SAR111View,
        },
        {
          path: 'forms/SAR134',
          name: 'incident-form-sar134',
          component: SAR134View,
        },
        {
          path: 'forms/iap',
          name: 'incident-forms-iap',
          component: IAPView,
        },
        {
          path: 'forms/:formType',
          name: 'incident-form-editor',
          component: PlaceholderView,
        },
        {
          path: 'clues',
          name: 'incident-clues',
          component: CluesView,
        },
        {
          path: 'comms',
          name: 'incident-comms',
          component: CommsView,
        },
        {
          path: 'audit',
          redirect: (to) => ({ name: 'incident-settings-audit', params: to.params }),
        },
        ...(import.meta.env.DEV ? [{
          path: 'dev-seed',
          name: 'dev-seed',
          component: DevSeedView,
        }] : []),
      ],
    },
  ],
})

export async function authGuard(to: RouteLocationNormalized): Promise<RouteLocationRaw | void> {
  const authStore = useAuthStore()

  // Wait for Firebase to resolve auth state before making any redirect decisions.
  // Without this, a hard reload sends unauthenticated users to /login before the
  // token is verified, causing a flash even when the user is already signed in.
  if (!authStore.authReady) {
    await new Promise<void>((resolve) => {
      const stop = watch(
        () => authStore.authReady,
        (ready) => { if (ready) { stop(); resolve() } },
      )
    })
  }

  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)

  if (requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login' }
  }

  if (authStore.isAuthenticated && to.name === 'login') {
    return { name: 'incident-list' }
  }

  if (authStore.isAuthenticated && !authStore.userProfile?.agency_id) {
    const profile = authStore.userProfile
    const alwaysAllowed =
      to.name === 'onboarding' || to.name === 'incident-join' || to.name === 'showcase'

    if (profile?.guest) {
      // Agency-less guests may operate only inside the incident they were invited
      // to. Firestore rules are the real access control; this is just navigation.
      const inTheirIncident =
        typeof to.params.id === 'string' && to.params.id === profile.guest_incident_id
      if (!alwaysAllowed && !inTheirIncident) {
        return profile.guest_incident_id
          ? { name: 'incident-overview', params: { id: profile.guest_incident_id } }
          : { name: 'onboarding' }
      }
    } else if (!alwaysAllowed) {
      return { name: 'onboarding' }
    }
  }

  if (to.meta.requiresSuperAdmin && !authStore.userProfile?.is_super_admin) {
    return { name: 'incident-list' }
  }
}

router.beforeEach(authGuard)

router.afterEach((to) => {
  analytics.then((instance) => {
    if (!instance) return
    logEvent(instance, 'page_view', {
      page_title: String(to.name ?? to.path),
      page_path: to.path,
      page_location: window.location.href,
    })
  })
})

export default router
